/**
 * Field-level validation for FmFormFlowPage descriptors.
 *
 * Split from `FmFormFlowPage.validation.ts` so neither file crosses the 500-line
 * gate. The descriptor, navigation, block and state-surface rules stay there;
 * everything that judges ONE control's own shape lives here.
 */
import {
  exactFormFlowKeys as exact,
  failFormFlow as fail,
  formFlowArray as array,
  formFlowBoolean as boolean,
  formFlowChoiceValue as choiceValue,
  formFlowObject as object,
  formFlowOneOf as oneOf,
  formFlowScalar as scalar,
  formFlowSelectValue as selectValue,
  formFlowText as text,
  optionalFormFlowText as optionalText,
  uniqueFormFlowIds as uniqueIds,
} from './FmFormFlowValidation.shared';

export function validation(value: unknown, path: string): void {
  if (value === undefined) return;
  const descriptor = object(value, path);
  exact(descriptor, new Set(['status', 'message']), path);
  oneOf(descriptor.status, ['unvalidated', 'valid', 'invalid', 'pending'], `${path}.status`);
  optionalText(descriptor.message, `${path}.message`);
  if (descriptor.status === 'invalid' && descriptor.message === undefined) {
    fail(`${path}.message`, 'is required for invalid state');
  }
}

export function state(value: Record<string, unknown>, path: string): void {
  if (value.state !== undefined) {
    oneOf(value.state, ['ready', 'loading', 'saving', 'saved', 'error', 'locked'], `${path}.state`);
    text(value.stateLabel, `${path}.stateLabel`);
  } else if (value.stateLabel !== undefined) {
    fail(`${path}.stateLabel`, 'requires state');
  }
}

export function blockBase(block: Record<string, unknown>, path: string): void {
  text(block.id, `${path}.id`);
  text(block.title, `${path}.title`);
  optionalText(block.description, `${path}.description`);
  state(block, path);
}

export function validateValue(value: unknown, path: string): void {
  const item = object(value, path);
  exact(item, new Set(['id', 'label', 'value', 'unit', 'sourceLabel', 'status']), path);
  text(item.id, `${path}.id`);
  text(item.label, `${path}.label`);
  scalar(item.value, `${path}.value`);
  optionalText(item.unit, `${path}.unit`);
  optionalText(item.sourceLabel, `${path}.sourceLabel`);
  if (item.status !== undefined) {
    oneOf(item.status, ['default', 'positive', 'warning', 'negative'], `${path}.status`);
  }
}

export function validateField(value: unknown, path: string): void {
  const field = object(value, path);
  text(field.kind, `${path}.kind`);
  text(field.id, `${path}.id`);
  text(field.label, `${path}.label`);
  validation(field.validation, `${path}.validation`);
  const shared = ['kind', 'id', 'label', 'value', 'helper', 'validation'];
  optionalText(field.helper, `${path}.helper`);
  if (field.kind === 'text') return validateTextField(field, path, shared);
  if (field.kind === 'number') return validateNumberField(field, path, shared);
  if (field.kind === 'date') return validateDateField(field, path, shared);
  if (field.kind === 'select') return validateSelectField(field, path, shared);
  if (field.kind === 'choice') return validateChoiceField(field, path, shared);
  if (field.kind === 'files') return validateFilesField(field, path);
  fail(`${path}.kind`, 'is not registered');
}

function validateTextField(
  field: Record<string, unknown>,
  path: string,
  shared: readonly string[],
): void {
  exact(field, new Set([...shared, 'placeholder', 'required', 'multiline', 'rows']), path);
  if (typeof field.value !== 'string') fail(`${path}.value`, 'must be text');
  optionalText(field.placeholder, `${path}.placeholder`);
  if (field.required !== undefined) boolean(field.required, `${path}.required`);
  if (field.multiline !== undefined) boolean(field.multiline, `${path}.multiline`);
  if (field.rows === undefined) return;
  const rows = field.rows;
  if (typeof rows !== 'number' || !Number.isInteger(rows) || rows < 1 || rows > 12) {
    fail(`${path}.rows`, 'must be an integer from 1 to 12');
  }
}

function validateNumberField(
  field: Record<string, unknown>,
  path: string,
  shared: readonly string[],
): void {
  exact(
    field,
    new Set([...shared, 'placeholder', 'required', 'min', 'max', 'step', 'precision']),
    path,
  );
  if (field.value !== null && (typeof field.value !== 'number' || !Number.isFinite(field.value))) {
    fail(`${path}.value`, 'must be a finite number or null');
  }
  optionalText(field.placeholder, `${path}.placeholder`);
  if (field.required !== undefined) boolean(field.required, `${path}.required`);
  validateFiniteNumberOptions(field, path);
  validateNumberBounds(field, path);
}

function validateFiniteNumberOptions(field: Record<string, unknown>, path: string): void {
  for (const key of ['min', 'max', 'step', 'precision'] as const) {
    if (
      field[key] !== undefined &&
      (typeof field[key] !== 'number' || !Number.isFinite(field[key]))
    ) {
      fail(`${path}.${key}`, 'must be a finite number');
    }
  }
  if (typeof field.step === 'number' && field.step <= 0)
    fail(`${path}.step`, 'must be greater than zero');
  if (
    typeof field.precision === 'number' &&
    (!Number.isInteger(field.precision) || field.precision < 0)
  ) {
    fail(`${path}.precision`, 'must be a non-negative integer');
  }
}

function validateNumberBounds(field: Record<string, unknown>, path: string): void {
  if (typeof field.min === 'number' && typeof field.max === 'number' && field.min > field.max) {
    fail(path, 'min must not exceed max');
  }
}

function validateDateField(
  field: Record<string, unknown>,
  path: string,
  shared: readonly string[],
): void {
  exact(field, new Set([...shared, 'placeholder']), path);
  if (field.value !== null && typeof field.value !== 'string')
    fail(`${path}.value`, 'must be an ISO date or null');
  optionalText(field.placeholder, `${path}.placeholder`);
}

function validateOptions(
  value: unknown,
  path: string,
  validateValue: (value: unknown, path: string) => void,
): void {
  const options = array(value, path);
  uniqueIds(options, path);
  options.forEach((value, index) => {
    const optionPath = `${path}[${index}]`;
    const option = object(value, optionPath);
    exact(option, new Set(['id', 'label', 'value', 'disabled']), optionPath);
    text(option.label, `${optionPath}.label`);
    validateValue(option.value, `${optionPath}.value`);
    if (option.disabled !== undefined) boolean(option.disabled, `${optionPath}.disabled`);
  });
}

function validateSelectField(
  field: Record<string, unknown>,
  path: string,
  shared: readonly string[],
): void {
  exact(field, new Set([...shared, 'placeholder', 'loading', 'options']), path);
  selectValue(field.value, `${path}.value`, true);
  optionalText(field.placeholder, `${path}.placeholder`);
  if (field.loading !== undefined) boolean(field.loading, `${path}.loading`);
  validateOptions(field.options, `${path}.options`, (value, valuePath) =>
    selectValue(value, valuePath, false),
  );
}

function validateChoiceField(
  field: Record<string, unknown>,
  path: string,
  shared: readonly string[],
): void {
  exact(field, new Set([...shared, 'presentation', 'options']), path);
  choiceValue(field.value, `${path}.value`);
  if (field.presentation !== undefined)
    oneOf(field.presentation, ['segmented', 'radio'], `${path}.presentation`);
  validateOptions(field.options, `${path}.options`, choiceValue);
}

function validateFilesField(field: Record<string, unknown>, path: string): void {
  exact(
    field,
    new Set([
      'kind',
      'id',
      'label',
      'prompt',
      'hint',
      'details',
      'accept',
      'multiple',
      'inputLabel',
      'files',
    ]),
    path,
  );
  text(field.prompt, `${path}.prompt`);
  text(field.inputLabel, `${path}.inputLabel`);
  optionalText(field.hint, `${path}.hint`);
  if (field.details !== undefined) {
    const details = object(field.details, `${path}.details`);
    exact(details, new Set(['label', 'body']), `${path}.details`);
    text(details.label, `${path}.details.label`);
    text(details.body, `${path}.details.body`);
  }
  optionalText(field.accept, `${path}.accept`);
  if (field.multiple !== undefined) boolean(field.multiple, `${path}.multiple`);
  const files = array(field.files, `${path}.files`);
  uniqueIds(files, `${path}.files`);
  files.forEach((value, fileIndex) => validateFile(value, `${path}.files[${fileIndex}]`));
}

function validateFile(value: unknown, path: string): void {
  const file = object(value, path);
  exact(
    file,
    new Set(['id', 'name', 'sizeLabel', 'status', 'statusLabel', 'progress', 'error']),
    path,
  );
  text(file.name, `${path}.name`);
  text(file.statusLabel, `${path}.statusLabel`);
  oneOf(file.status, ['queued', 'uploading', 'ready', 'warning', 'error'], `${path}.status`);
  optionalText(file.sizeLabel, `${path}.sizeLabel`);
  optionalText(file.error, `${path}.error`);
  if (
    file.progress !== undefined &&
    (typeof file.progress !== 'number' || file.progress < 0 || file.progress > 100)
  ) {
    fail(`${path}.progress`, 'must be between 0 and 100');
  }
}
